import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

const KOLOM_CSV = [
  { kunci: "sort_order", judul: "Sort" },
  { kunci: "size_code", judul: "Size Code" },
  { kunci: "surcharge_amount_idr", judul: "Surcharge (IDR)" },
  { kunci: "applies_from_size_level", judul: "From Level" },
  { kunci: "effective_from", judul: "Berlaku Dari" },
  { kunci: "effective_to", judul: "Berlaku Sampai" },
  { kunci: "active", judul: "Aktif", ambil: (r) => (r.active ? "Ya" : "Tidak") },
];

export default function MasterSizeSurcharge() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/size-surcharge");
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal memuat"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const daftar = useDaftar(items, {
    kunciCari: ["size_code"],
    urutAwal: { kunci: "sort_order", arah: "asc" },
  });

  const handleDelete = async () => {
    try { await api.delete(`/master/size-surcharge/${confirmDel.id}`); toast.success("Aturan size dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal hapus"); }
    finally { setConfirmDel(null); }
  };

  return (
    <div className="space-y-6" data-testid="master-size-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Biaya Tambahan Ukuran</h1>
          <p className="text-sm text-muted-foreground mt-1">Surcharge per ukuran (XL/2XL/3XL/dst). Diterapkan per pcs di FA.</p>
        </div>
        {isOwner && <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="size-new-btn"><Plus size={16} className="mr-1" /> Tambah</Button>}
      </div>
      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari size code…" testId="size-palang"
                      ekspor={{ nama: "master_size_surcharge", kolom: KOLOM_CSV }} />
        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="sort_order" className="text-left px-3 py-2">Sort</ThUrut>
                  <ThUrut daftar={daftar} kunci="size_code" className="text-left px-3 py-2">Size Code</ThUrut>
                  <ThUrut daftar={daftar} kunci="surcharge_amount_idr" className="text-right px-3 py-2" align="right">Surcharge (IDR)</ThUrut>
                  <ThUrut daftar={daftar} kunci="applies_from_size_level" className="text-right px-3 py-2" align="right">From Level</ThUrut>
                  <th className="text-left px-3 py-2">Berlaku</th>
                  <ThUrut daftar={daftar} kunci="active" className="text-left px-3 py-2">Status</ThUrut>
                  {isOwner && <th></th>}
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && <tr><td colSpan={isOwner ? 7 : 6} className="px-3 py-8 text-center text-muted-foreground">
                  {daftar.cari.trim() ? "Tidak ada yang cocok." : "Belum ada aturan."}
                </td></tr>}
                {daftar.tampil.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`size-row-${r.id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.sort_order}</td>
                    <td className="px-3 py-2 font-mono font-medium">{r.size_code}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatIDR(r.surcharge_amount_idr)}</td>
                    <td className="px-3 py-2 text-right">{r.applies_from_size_level ?? "—"}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.effective_from || "—"} → {r.effective_to || "∞"}</td>
                    <td className="px-3 py-2">{r.active ? <StatusBadge tone="success">Aktif</StatusBadge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                    {isOwner && (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`size-edit-${r.id}`}><Edit size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(r)} className="text-danger-soft-foreground" data-testid={`size-delete-${r.id}`}><Trash2 size={14} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="size-paginasi" />
          </div>
        )}
      </Card>
      {showForm && <SizeFormModal rule={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); load(); }} />}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus Aturan Size?</AlertDialogTitle><AlertDialogDescription>"{confirmDel?.size_code}" akan dihapus.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="size-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SizeFormModal({ rule, onClose, onSaved }) {
  const [f, setF] = useState({
    size_code: rule?.size_code || "",
    surcharge_amount_idr: rule?.surcharge_amount_idr ?? 0,
    sort_order: rule?.sort_order ?? 0,
    applies_from_size_level: rule?.applies_from_size_level ?? "",
    effective_from: rule?.effective_from || "",
    effective_to: rule?.effective_to || "",
    active: rule?.active ?? true,
    notes: rule?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.size_code.trim()) { toast.error("Size code wajib"); return; }
    setSaving(true);
    try {
      const body = { ...f, surcharge_amount_idr: parseInt(f.surcharge_amount_idr) || 0, sort_order: parseInt(f.sort_order) || 0,
        applies_from_size_level: f.applies_from_size_level === "" ? null : parseInt(f.applies_from_size_level),
        effective_from: f.effective_from || null, effective_to: f.effective_to || null, notes: f.notes || null };
      if (rule) await api.patch(`/master/size-surcharge/${rule.id}`, body);
      else await api.post("/master/size-surcharge", body);
      toast.success("Disimpan"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="size-form-modal">
        <DialogHeader><DialogTitle>{rule ? "Edit Aturan Size" : "Tambah Aturan Size"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2"><Label>Size Code *</Label><Input value={f.size_code} onChange={(e) => upd("size_code", e.target.value)} placeholder="cth: 2XL" data-testid="size-form-code" /></div>
          <div><Label>Surcharge (IDR) *</Label><Input type="number" min={0} value={f.surcharge_amount_idr} onChange={(e) => upd("surcharge_amount_idr", e.target.value)} data-testid="size-form-amount" /></div>
          <div><Label>Sort Order</Label><Input type="number" value={f.sort_order} onChange={(e) => upd("sort_order", e.target.value)} /></div>
          <div><Label>Applies From Size Level</Label><Input type="number" value={f.applies_from_size_level} onChange={(e) => upd("applies_from_size_level", e.target.value)} placeholder="cth: 5" /></div>
          <div className="col-span-2 grid grid-cols-2 gap-2">
            <div><Label>Berlaku Dari</Label><Input type="date" value={f.effective_from} onChange={(e) => upd("effective_from", e.target.value)} /></div>
            <div><Label>Berlaku Sampai</Label><Input type="date" value={f.effective_to} onChange={(e) => upd("effective_to", e.target.value)} /></div>
          </div>
          <div className="col-span-2 flex items-center gap-2"><Checkbox checked={f.active} onCheckedChange={(v) => upd("active", !!v)} id="szactive" /><Label htmlFor="szactive" className="cursor-pointer">Aktif</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="size-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
